/**
 * Actual vs Tendered Cost Breakdown Section
 * Shows cost comparison table with tendered vs actual values
 * Change 5: Manual entry now, auto-populated by Nanonets/Xero in future
 */
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { 
  ChevronDown, 
  ChevronUp, 
  Edit, 
  Save, 
  X, 
  Loader2, 
  TrendingUp,
  TrendingDown,
  Pencil
} from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '—';
  const dp = Math.abs(value) < 10000 && Math.round(value) !== value ? 2 : 0;
  return new Intl.NumberFormat('en-GB', { 
    style: 'currency', 
    currency: 'GBP',
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  }).format(value);
};

export function CostBreakdownSection({ 
  projectId,
  api,
  canEdit,
  onUpdate
}) {
  const [expanded, setExpanded] = useState(true);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [costData, setCostData] = useState(null);
  const [editValues, setEditValues] = useState({});

  // Load cost breakdown data
  useEffect(() => {
    if (!projectId) return;
    
    const loadData = async () => {
      try {
        const response = await api.get(`/projects/${projectId}/cost-breakdown`);
        setCostData(response.data);
      } catch (error) {
        console.error('Failed to load cost breakdown:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [projectId, api]);

  // Initialize edit values when entering edit mode
  const startEdit = () => {
    const values = {};
    costData?.rows?.forEach(row => {
      const key = row.category.toLowerCase().replace(/\s+/g, '_');
      values[`tendered_${key}`] = row.tendered_value || '';
      values[`actual_${key}`] = row.actual_value || '';
    });
    setEditValues(values);
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditValues({});
  };

  const handleValueChange = (field, value) => {
    setEditValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setSubmitting(true);
    try {
      // Update actual costs - all 7 categories
      await api.put(`/projects/${projectId}/actual-costs`, {
        actual_labour_cost: parseFloat(editValues.actual_labour) || 0,
        actual_materials_cost: parseFloat(editValues.actual_materials) || 0,
        actual_plant_cost: parseFloat(editValues.actual_plant) || 0,
        actual_subcontractor_cost: parseFloat(editValues.actual_subcontractors) || 0,
        actual_prelims_cost: parseFloat(editValues.actual_prelims) || 0,
        actual_prosums_cost: parseFloat(editValues.actual_pro_sums) || 0,
        actual_op_cost: parseFloat(editValues['actual_o&p']) || 0,
        labour_source: 'manual',
        materials_source: 'manual',
        plant_source: 'manual',
        subcontractor_source: 'manual',
        prelims_source: 'manual',
        prosums_source: 'manual',
        op_source: 'manual'
      });
      
      toast.success('Cost breakdown updated');
      setEditMode(false);
      
      // Reload data
      const response = await api.get(`/projects/${projectId}/cost-breakdown`);
      setCostData(response.data);
      
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update costs');
    } finally {
      setSubmitting(false);
    }
  };

  const getVarianceColor = (variance) => {
    if (variance > 0) return 'text-green-400';
    if (variance < 0) return 'text-red-400';
    return 'text-gray-400';
  };

  const getVarianceIcon = (variance) => {
    if (variance > 0) return <TrendingUp className="w-3 h-3 text-green-400" />;
    if (variance < 0) return <TrendingDown className="w-3 h-3 text-red-400" />;
    return null;
  };

  const SourceBadge = ({ source }) => {
    if (source === 'live') {
      return (
        <Badge className="ml-1.5 px-1.5 py-0 text-[9px] font-mono uppercase tracking-wider bg-blue-500/15 text-blue-400 border-blue-500/30" title="Live from the Site Diary / Cost Ledger" data-testid="chip-live">
          Live
        </Badge>
      );
    }
    if (source === 'auto' || source === 'tender') {
      return (
        <Badge className="ml-1.5 px-1.5 py-0 text-[9px] font-mono uppercase tracking-wider bg-purple-500/15 text-purple-400 border-purple-500/30" title="From the linked tender">
          Tender
        </Badge>
      );
    }
    return (
      <Badge className="ml-1.5 px-1.5 py-0 text-[9px] font-mono uppercase tracking-wider bg-gray-500/15 text-gray-400 border-gray-500/30" title="Entered manually" data-testid="chip-manual">
        Manual
      </Badge>
    );
  };

  if (loading) {
    return (
      <Card className="bg-[#2A3240] border-white/10">
        <CardContent className="py-8 text-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#F5A623] mx-auto" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[#2A3240] border-white/10">
      <CardHeader 
        className="pb-3 cursor-pointer hover:bg-white/5 transition-colors rounded-t-lg"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2 text-lg" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Actual vs Tendered
            {costData?.has_linked_tender && costData?.tender_ref && (
              <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-xs">
                Linked: {costData.tender_ref}
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-3">
            {/* Total Variance Indicator */}
            {costData && (
              <div className={`flex items-center gap-1 ${getVarianceColor(costData.total_variance)}`}>
                {getVarianceIcon(costData.total_variance)}
                <span className="text-sm font-medium">
                  {costData.total_variance >= 0 ? '+' : ''}{formatCurrency(costData.total_variance)}
                </span>
                <span className="text-xs">
                  ({costData.total_variance_percent >= 0 ? '+' : ''}{costData.total_variance_percent}%)
                </span>
              </div>
            )}
            {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </div>
        </div>
      </CardHeader>
      
      {expanded && (
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <p className="text-gray-400 text-sm">
              Labour, Materials and Plant are <span className="text-blue-400">live</span> from the Site Diary and Cost Ledger.
              {costData?.has_linked_tender
                ? ' Tendered values come from the linked tender; the other actuals are entered by hand.'
                : ' Other actuals are entered by hand — link a tender to auto-fill the tendered column.'}
            </p>
            {canEdit && !editMode && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => { e.stopPropagation(); startEdit(); }}
                className="text-gray-400 hover:text-[#F5A623] h-7"
                data-testid="edit-cost-breakdown-btn"
              >
                <Edit className="w-3 h-3 mr-1" />
                Edit Costs
              </Button>
            )}
            {editMode && (
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={cancelEdit}
                  className="text-gray-400 hover:text-white h-7"
                >
                  <X className="w-3 h-3 mr-1" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={submitting}
                  className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] h-7"
                  data-testid="save-cost-breakdown-btn"
                >
                  {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Save className="w-3 h-3 mr-1" />Save</>}
                </Button>
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-gray-500 pb-3 font-medium text-xs uppercase w-[20%]">Category</th>
                  <th className="text-right text-gray-500 pb-3 font-medium text-xs uppercase w-[20%]">Tendered £</th>
                  <th className="text-right text-gray-500 pb-3 font-medium text-xs uppercase w-[20%]">Actual £ to Date</th>
                  <th className="text-right text-gray-500 pb-3 font-medium text-xs uppercase w-[20%]">Variance £</th>
                  <th className="text-right text-gray-500 pb-3 font-medium text-xs uppercase w-[20%]">Used</th>
                </tr>
              </thead>
              <tbody>
                {costData?.rows?.map((row, index) => {
                  const key = row.category.toLowerCase().replace(/\s+/g, '_');
                  const live = row.actual_source === 'live';
                  const used = row.tendered_value > 0 ? Math.round((row.actual_value / row.tendered_value) * 100) : 0;
                  
                  return (
                    <tr key={row.category} className="border-b border-white/5" data-testid={`avt-row-${key}`}>
                      <td className="py-3 text-white font-medium">
                        {row.category}
                        {key === 'labour' && costData?.labour_detail && (
                          <div className="text-[11px] text-gray-500 font-normal mt-0.5 leading-snug" data-testid="avt-labour-detail">
                            Employees (diary) {formatCurrency(costData.labour_detail.employee_diary)} + invoiced {formatCurrency(costData.labour_detail.invoiced)}
                            {costData.labour_detail.awaiting_invoice > 0 && (
                              <span className="text-amber-400/90"> · sub-contractor diary {formatCurrency(costData.labour_detail.awaiting_invoice)} awaiting invoice (not counted)</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="py-3 text-right">
                        {editMode ? (
                          <div className="flex items-center justify-end gap-1">
                            <Input
                              type="number"
                              value={editValues[`tendered_${key}`] || ''}
                              onChange={(e) => handleValueChange(`tendered_${key}`, e.target.value)}
                              className="bg-[#1E2530] border-white/20 text-white h-7 w-24 text-right"
                              placeholder="0"
                              disabled={row.tendered_source === 'tender'}
                            />
                          </div>
                        ) : (
                          <div className="flex items-center justify-end">
                            <span className="text-white">{formatCurrency(row.tendered_value)}</span>
                            <SourceBadge source={row.tendered_source} />
                            {canEdit && row.tendered_source !== 'tender' && (
                              <Pencil className="w-3 h-3 text-gray-500 ml-1" />
                            )}
                          </div>
                        )}
                      </td>
                      <td className="py-3 text-right">
                        {editMode && !live ? (
                          <Input
                            type="number"
                            value={editValues[`actual_${key}`] || ''}
                            onChange={(e) => handleValueChange(`actual_${key}`, e.target.value)}
                            className="bg-[#1E2530] border-white/20 text-white h-7 w-24 ml-auto text-right"
                            placeholder="0"
                          />
                        ) : (
                          <div className="flex items-center justify-end">
                            <span className="text-white">{formatCurrency(row.actual_value)}</span>
                            <SourceBadge source={row.actual_source} />
                            {canEdit && !live && (
                              <Pencil className="w-3 h-3 text-gray-500 ml-1" />
                            )}
                          </div>
                        )}
                      </td>
                      <td className={`py-3 text-right ${getVarianceColor(row.variance_value)}`}>
                        <div className="flex items-center justify-end gap-1">
                          {getVarianceIcon(row.variance_value)}
                          <span>{row.variance_value >= 0 ? '+' : ''}{formatCurrency(row.variance_value)}</span>
                        </div>
                      </td>
                      <td className="py-3 text-right">
                        {row.tendered_value > 0 ? (
                          <div className="inline-flex items-center gap-2">
                            <div className="w-16 h-1.5 rounded-full bg-white/10 overflow-hidden">
                              <div className={`h-full ${used > 100 ? 'bg-red-400' : 'bg-[#F5A623]'}`} style={{ width: `${Math.min(100, used)}%` }} />
                            </div>
                            <span className={`text-xs tabular-nums w-10 text-right ${used > 100 ? 'text-red-400' : 'text-gray-400'}`}>{used}%</span>
                          </div>
                        ) : <span className="text-gray-500">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-white/20 font-bold">
                  <td className="pt-4 text-white uppercase text-xs">TOTAL</td>
                  <td className="pt-4 text-right text-white text-lg">{formatCurrency(costData?.total_tendered)}</td>
                  <td className="pt-4 text-right text-white text-lg">{formatCurrency(costData?.total_actual)}</td>
                  <td className={`pt-4 text-right text-lg ${getVarianceColor(costData?.total_variance)}`}>
                    <div className="flex items-center justify-end gap-1">
                      {getVarianceIcon(costData?.total_variance)}
                      <span>{(costData?.total_variance || 0) >= 0 ? '+' : ''}{formatCurrency(costData?.total_variance)}</span>
                    </div>
                  </td>
                  <td className={`pt-4 text-right text-lg ${getVarianceColor(costData?.total_variance_percent)}`}>
                    {costData?.total_tendered > 0 
                      ? `${Math.round(((costData?.total_actual || 0) / costData.total_tendered) * 100)}%`
                      : '—'
                    }
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <p className="text-xs text-gray-500 mt-4 pt-4 border-t border-white/10">
            Live rows update as receipts are captured and labour is logged in the Site Diary — they can't be overtyped here.
          </p>
        </CardContent>
      )}
    </Card>
  );
}
